// @flow
import React, { Component, Children, type Context } from 'react';
import type { Node } from 'react';
import { createStore as reduxCreateStore, combineReducers, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import createSagaMiddleware from 'redux-saga';
import type { ContextRouter } from 'react-router-dom';
import { Broadcast } from 'react-broadcast';
import { composeWithDevTools } from 'redux-devtools-extension';
import type { Middleware, Reducer, Store } from 'redux';
import { I18n } from '@lingui/react';
import type { I18n as I18nType } from '@lingui/core';
// cyclic dependency: https://roverdotcom.atlassian.net/browse/DEV-49358
// eslint-disable-next-line import/no-cycle
import { reducer as lifted } from '@rover/react-lib/lib/lifted-components/redux';

const DEFAULT_MIDDLEWARE = [thunk.withExtraArgument({})];

export const BROADCAST_CHANNEL = 'redux-provider';
export const InitialStateContext: Context<any> = React.createContext();
export const createStore = (
  reducers: { [string]: (state: any, action: any) => any },
  // eslint-disable-next-line no-underscore-dangle, rover/no-platform-specific-globals-or-imports
  initialState: any = window.__ROVER_INITIAL_DATA__,
  middleware: Array<Middleware<any, any>> = DEFAULT_MIDDLEWARE,
  name: string
): Store<any, any> =>
  (reduxCreateStore(
    combineReducers({
      ...reducers,
      lifted,
    }),
    initialState,
    composeWithDevTools({ name })(applyMiddleware(...middleware))
  ): any);

export type Props = {
  reducers: { [string]: Reducer<any, any> },
  children: Node,
  initialState: any,
  name: string,
  sagas: Array<Function>,
  routerProps: ?ContextRouter,
  store?: *,
  query?: *,
  history?: *,
  routeContext?: *,
};

export type InnerProviderProp = Props & {
  i18n: I18nType,
};

class InnerProvider extends Component<InnerProviderProp> {
  reducers: { [string]: (state: any, action: any) => any };

  reduxStore: any;

  sagaMiddleWare: Middleware<any, any> & { run: Function };

  constructor(props: InnerProviderProp) {
    super(props);
    if (props.store) {
      this.reduxStore = props.store;
    } else {
      this.reducers = props.reducers;
      this.sagaMiddleWare = createSagaMiddleware();
      this.reduxStore = createStore(
        props.reducers,
        props.initialState,
        [
          thunk.withExtraArgument({
            routerProps: props.routerProps,
            i18n: props.i18n,
            query: props.query,
            history: props.history,
            expressParams: (props.routeContext || {}).expressParams,
          }),
          this.sagaMiddleWare,
        ],
        props.name
      );
      this.registerSagas();
    }
  }

  registerSagas() {
    if (this.sagaMiddleWare) {
      this.props.sagas.forEach(saga => this.sagaMiddleWare.run(saga, this.props.routerProps));
    }
  }

  getContext() {
    return {
      reduxStore: this.reduxStore,
    };
  }

  render() {
    return (
      <Broadcast channel={BROADCAST_CHANNEL} value={this.getContext()}>
        {Children.only(this.props.children)}
      </Broadcast>
    );
  }
}

const Provider = (props: Props) => (
  <I18n>
    {({ i18n }) => (
      <InitialStateContext.Consumer>
        {initialState => <InnerProvider {...props} i18n={i18n} initialState={initialState} />}
      </InitialStateContext.Consumer>
    )}
  </I18n>
);

Provider.defaultProps = {
  initialState: undefined,
  name: '',
  sagas: [],
  routerProps: undefined,
  store: undefined,
  query: undefined,
  history: undefined,
  routeContext: undefined,
};

export default Provider;



// WEBPACK FOOTER //
// ../react-lib/src/components/utils/Redux/Provider.jsx